import moment from 'moment';
import ArrayService from 'shared/services/ArrayService';
import { INCREMENTABLE_DISH_TYPES } from './constants';
import { Client, ClientIdentity, Meal, EditedMeal, MealMenu } from './types';

export function getWeekDaysArray(firstWeekDay: string): Array<Date> {
    var weekDays: Array<Date> = [];
    for (var i = 1; i < 8; i++) {
        weekDays.push(moment(firstWeekDay).weekday(i).toDate());
    }
    return weekDays;
}

export interface MealAlert {
    clientIdentity: Id;
    meal: EditedMeal;
    message: string;
}
interface getMealsAlertsOptions {
    editedMeals: EditedMeal[];
    currentMeals: Meal[];
    mealMenus: MealMenu[];
    client: Client | undefined;
}

export function getMealsAlerts({
    editedMeals,
    currentMeals,
    mealMenus,
    client,
}: getMealsAlertsOptions) {
    const alerts: MealAlert[] = [];

    const meals = [
        ...currentMeals
            .filter(
                (meal) => editedMeals.findIndex((e) => e.id === meal.id) === -1,
            )
            .map((meal) => {
                return {
                    ...meal,
                    command: meal.command.id,
                    menu: meal.menu.id,
                    clientIdentity: meal.clientIdentity.id,
                    dishes: meal.dishes.map((d) => ({
                        dish: d.dish.id,
                        quantity: d.quantity,
                        dishType: d.dishType
                    })),
                };
            }),
        ...editedMeals
            .filter((e) => e.default !== true)
            .map(({ default: _, ...rest }) => rest),
    ];

    function mealContainsAllergies(
        clientIdentity: ClientIdentity,
        menu: MealMenu,
        meal: EditedMeal,
    ) {
        const selectedDishes = menu.dishes
            .map((m) => m.dish)
            .filter(
                (d) => meal.dishes.findIndex((dish) => dish.dish === d.id) > -1,
            );

        if (!selectedDishes) {
            return null;
        }

        const dishAllergies = selectedDishes
            .reduce(
                (prev, curr) => [...prev, ...curr.allergies],
                [] as {
                    id: Id;
                    content: string;
                }[],
            )
            .filter(ArrayService.filterUniqueEntity);

        return (
            clientIdentity &&
            dishAllergies.findIndex(
                (a) =>
                    clientIdentity.allergies.findIndex((ca) => ca.id === a.id) >
                    -1,
            ) > -1
        );
    }

    function mealIsComplete(menu: MealMenu, meal: EditedMeal) {
        // We look at the dish types that must be single (excluding extras, desserts, etc.)
        return (
            menu.dishTypes
                .filter(
                    (d) =>
                        d !== 'extra' && !INCREMENTABLE_DISH_TYPES.includes(d),
                )
                .findIndex((dishType) => {
                    const mealDishes = meal.dishes
                        .map(
                            (mealDish) =>
                                menu.dishes.find(
                                    (menuDish) =>
                                        menuDish.dish.id === mealDish.dish,
                                )?.dish,
                        )
                        .filter((d) => !!d);

                    // If the main dish has no side-dish (e.g. Tartiflette, Cassoulet)
                    // Then we should ignore the side dish.
                    if (dishType === 'side-dish') {
                        const mainDish = mealDishes.find(
                            (dish) => dish && dish.type === 'dish',
                        );
                        if (mainDish && mainDish.noSideDish) {
                            return false;
                        }
                    }
                    return (
                        mealDishes.findIndex(
                            (dish) => dish && dish.type === dishType,
                        ) === -1
                    );
                }) === -1
        );
    }

    function mealIncrementableDishesAreValid(menu: MealMenu, meal: EditedMeal) {
        // Then we look at the incrementable dish types
        const incrementableDishTypes = menu.dishTypes.filter((d) =>
            INCREMENTABLE_DISH_TYPES.includes(d),
        );

        const totalWithoutBread = meal.dishes
            .filter((dish) => {
                const mealDish = menu.dishes.find(
                    (menuDish) => menuDish.dish.id === dish.dish,
                )?.dish;
                return (
                    mealDish &&
                    mealDish.type !== 'bread' &&
                    incrementableDishTypes.includes(mealDish.type)
                );
            })
            .reduce((prev, curr) => prev + curr.quantity, 0);

        const totalBread = meal.dishes
            .filter((dish) => {
                const mealDish = menu.dishes.find(
                    (menuDish) => menuDish.dish.id === dish.dish,
                )?.dish;
                return mealDish && mealDish.type === 'bread';
            })
            .reduce((prev, curr) => prev + curr.quantity, 0);

        return (
            totalWithoutBread + totalBread === incrementableDishTypes.length ||
            (totalWithoutBread === incrementableDishTypes.length && menu.mealDefinition.name != 'Complet' && totalBread === 0) ||
            (totalWithoutBread === incrementableDishTypes.length - 1 && menu.mealDefinition.name === 'Complet' && totalBread === 0)
        );
    }

    meals.forEach((meal) => {
        const menu = mealMenus.find((m) => m.id === meal.menu);
        const clientIdentity = client?.clientIdentities.find(
            (c) => c.id === meal.clientIdentity,
        );
        if (!menu || !clientIdentity) {
            return;
        }
        if (mealContainsAllergies(clientIdentity, menu, meal)) {
            alerts.push({
                clientIdentity: clientIdentity.id,
                meal,
                message: 'Allergènes',
            });
        }
        // if (mealIsSingle(menu, meal)) {
        //     alerts.push({
        //         clientIdentity: clientIdentity.id,
        //         meal,
        //         message: 'Plat seul',
        //     });
        // }
        if (!mealIsComplete(menu, meal)) {
            alerts.push({
                clientIdentity: clientIdentity.id,
                meal,
                message: 'Incomplète',
            });
        } else if (!mealIncrementableDishesAreValid(menu, meal)) {
            alerts.push({
                clientIdentity: clientIdentity.id,
                meal,
                message: 'Nombre de fromage / dessert / pain incorrect',
            });
        }
    });

    return alerts;
}
export function areAllMealsEmpty({
                                     editedMeals,
                                     currentMeals,
                                     mealMenus,
                                     client,
                                 }: getMealsAlertsOptions)
{
    const meals = [
        ...currentMeals
            .filter(
                (meal) => editedMeals.findIndex((e) => e.id === meal.id) === -1,
            )
            .map((meal) => {
                return {
                    ...meal,
                    command: meal.command.id,
                    menu: meal.menu.id,
                    clientIdentity: meal.clientIdentity.id,
                    dishes: meal.dishes.map((d) => ({
                        dish: d.dish.id,
                        quantity: d.quantity,
                        dishType: d.dishType
                    })),
                };
            }),
        ...editedMeals
            .filter((e) => e.default !== true)
            .map(({ default: _, ...rest }) => rest),
    ];
    return meals.every((meal) => meal.dishes.length === 0);
}


export function getDefaultDishes(
    clientIdentity: ClientIdentity,
    weekDay: string,
    mealPeriod: string,
    menu: MealMenu,
) {
    return (
        clientIdentity.defaultDishes
            .find(
                (d) =>
                    d.weekDay === weekDay &&
                    d.mealPeriod === mealPeriod,
            )
            ?.dishes.filter(
                (d) =>
                    menu.dishes.findIndex(
                        (md) => md.dish.id === d.dish.id,
                    ) > -1,
            )
            .map((d) => ({
                dish: d.dish.id,
                quantity: d.quantity,
            })) || []
    );
}